import React from 'react';

// Auto-capitalise first letter of each word; allow letters, spaces, commas (for language lists)
const capitaliseWords = (value, allowCommas = false) => {
  const pattern = allowCommas ? /[^a-zA-Z\s,]/g : /[^a-zA-Z\s]/g;
  const cleaned = value.replace(pattern, '');
  // Capitalise first letter after start, space, or comma
  return cleaned.replace(/(^|[\s,])([a-z])/g, (_, sep, ch) => sep + ch.toUpperCase());
};

// Strip non-alpha/space only (no commas) — for university name, stream
const capitaliseWordsStrict = (value) => {
  const cleaned = value.replace(/[^a-zA-Z\s]/g, '');
  return cleaned.replace(/\b\w/g, (ch) => ch.toUpperCase());
};

// Capitalise letters only, keep digits and spaces — for timing field (e.g. "8 Am To 6 Pm")
const capitaliseTimingWords = (value) => {
  // Allow letters, digits, spaces, colon, hyphen, AM/PM
  const cleaned = value.replace(/[^a-zA-Z0-9\s:]/g, '');
  return cleaned.replace(/\b([a-z])/g, (ch) => ch.toUpperCase());
};

export const CourseTable = ({ courses, setCourses, errors, touched, onBlur }) => {
  const handleCourseChange = (index, field, value) => {
    const updatedCourses = [...courses];
    updatedCourses[index][field] = value;
    setCourses(updatedCourses);
  };

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    marginBottom: '40px'
  };

  const thStyle = {
    padding: '15px',
    textAlign: 'left',
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
    color: 'var(--text-secondary)',
    background: 'var(--neutral-cream)'
  };

  const tdStyle = {
    padding: '20px',
    borderBottom: '1px solid var(--neutral-light)'
  };

  const errorStyle = {
    color: '#e74c3c',
    fontSize: '12px',
    marginTop: '4px',
    display: 'block'
  };

  return (
    <div>
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={thStyle}>University Name *</th>
            <th style={thStyle}>Academic Year *</th>
            <th style={thStyle}>Stream *</th>
            <th style={thStyle}>Timing *</th>
            <th style={thStyle}>University Phone No. *</th>
          </tr>
        </thead>
        <tbody>
          {courses.map((course, index) => (
            <tr key={index}>
              <td style={tdStyle}>
                <input 
                  type="text" 
                  className={`form-input ${errors[`course_${index}_universityName`] ? 'form-input-error' : ''}`}
                  placeholder="EXAMPLE - PUNE UNIVERSITY" 
                  style={{ marginBottom: 0 }}
                  value={course.universityName}
                  onChange={(e) => handleCourseChange(index, 'universityName', capitaliseWordsStrict(e.target.value))}
                  onBlur={() => onBlur(`course_${index}_universityName`)}
                />
                {touched[`course_${index}_universityName`] && errors[`course_${index}_universityName`] && (
                  <span style={errorStyle}>{errors[`course_${index}_universityName`]}</span>
                )}
              </td>
              <td style={tdStyle}>
                <input 
                  type="text" 
                  className={`form-input ${errors[`course_${index}_academicYear`] ? 'form-input-error' : ''}`}
                  placeholder="EXAMPLE - 2015-16" 
                  style={{ marginBottom: 0 }}
                  value={course.academicYear}
                  onChange={(e) => handleCourseChange(index, 'academicYear', e.target.value)}
                  onBlur={() => onBlur(`course_${index}_academicYear`)}
                />
                {touched[`course_${index}_academicYear`] && errors[`course_${index}_academicYear`] && (
                  <span style={errorStyle}>{errors[`course_${index}_academicYear`]}</span>
                )}
              </td>
              <td style={tdStyle}>
                <input 
                  type="text" 
                  className={`form-input ${errors[`course_${index}_stream`] ? 'form-input-error' : ''}`}
                  placeholder="EXAMPLE - MECH ENGINEERING" 
                  style={{ marginBottom: 0 }}
                  value={course.stream}
                  onChange={(e) => handleCourseChange(index, 'stream', capitaliseWordsStrict(e.target.value))}
                  onBlur={() => onBlur(`course_${index}_stream`)}
                />
                {touched[`course_${index}_stream`] && errors[`course_${index}_stream`] && (
                  <span style={errorStyle}>{errors[`course_${index}_stream`]}</span>
                )}
              </td>
              <td style={tdStyle}>
                <input 
                  type="text" 
                  className={`form-input ${errors[`course_${index}_timing`] ? 'form-input-error' : ''}`}
                  placeholder="EXAMPLE - 8 AM TO 6 PM" 
                  style={{ marginBottom: 0 }}
                  value={course.timing}
                  onChange={(e) => handleCourseChange(index, 'timing', capitaliseTimingWords(e.target.value))}
                  onBlur={() => onBlur(`course_${index}_timing`)}
                />
                {touched[`course_${index}_timing`] && errors[`course_${index}_timing`] && (
                  <span style={errorStyle}>{errors[`course_${index}_timing`]}</span>
                )}
              </td>
              <td style={tdStyle}>
                <input 
                  type="text" 
                  className={`form-input ${errors[`course_${index}_phoneNo`] ? 'form-input-error' : ''}`}
                  placeholder="EXAMPLE - 20124XXXXX" 
                  style={{ marginBottom: 0 }}
                  value={course.phoneNo}
                  onChange={(e) => handleCourseChange(index, 'phoneNo', e.target.value.replace(/\D/g, ''))}
                  onBlur={() => onBlur(`course_${index}_phoneNo`)}
                  maxLength="15"
                />
                {touched[`course_${index}_phoneNo`] && errors[`course_${index}_phoneNo`] && (
                  <span style={errorStyle}>{errors[`course_${index}_phoneNo`]}</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const LanguageTable = ({ languages, setLanguages, errors, touched, onBlur }) => {
  const handleLanguageChange = (field, value) => {
    setLanguages({ ...languages, [field]: value });
  };

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    marginBottom: '40px'
  };

  const thStyle = {
    padding: '15px',
    textAlign: 'left',
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
    color: 'var(--text-secondary)',
    background: 'var(--neutral-cream)'
  };

  const tdStyle = {
    padding: '20px'
  };

  const errorStyle = {
    color: '#e74c3c',
    fontSize: '12px',
    marginTop: '4px',
    display: 'block'
  };

  return (
    <div>
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={thStyle}>To Write *</th>
            <th style={thStyle}>To Speak *</th>
            <th style={thStyle}>To Read *</th>
            <th style={thStyle}>Mother Tongue *</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={tdStyle}>
              <input 
                type="text" 
                className={`form-input ${errors.toWrite ? 'form-input-error' : ''}`}
                placeholder="EXAMPLE - ENGLISH, HINDI" 
                style={{ marginBottom: 0 }}
                value={languages.toWrite}
                onChange={(e) => handleLanguageChange('toWrite', capitaliseWords(e.target.value, true))}
                onBlur={() => onBlur('toWrite')}
              />
              {touched.toWrite && errors.toWrite && (
                <span style={errorStyle}>{errors.toWrite}</span>
              )}
            </td>
            <td style={tdStyle}>
              <input 
                type="text" 
                className={`form-input ${errors.toSpeak ? 'form-input-error' : ''}`}
                placeholder="EXAMPLE - ENGLISH, MARATHI" 
                style={{ marginBottom: 0 }}
                value={languages.toSpeak}
                onChange={(e) => handleLanguageChange('toSpeak', capitaliseWords(e.target.value, true))}
                onBlur={() => onBlur('toSpeak')}
              />
              {touched.toSpeak && errors.toSpeak && (
                <span style={errorStyle}>{errors.toSpeak}</span>
              )}
            </td>
            <td style={tdStyle}>
              <input 
                type="text" 
                className={`form-input ${errors.toRead ? 'form-input-error' : ''}`}
                placeholder="EXAMPLE - ENGLISH, HINDI" 
                style={{ marginBottom: 0 }}
                value={languages.toRead}
                onChange={(e) => handleLanguageChange('toRead', capitaliseWords(e.target.value, true))}
                onBlur={() => onBlur('toRead')}
              />
              {touched.toRead && errors.toRead && (
                <span style={errorStyle}>{errors.toRead}</span>
              )}
            </td>
            <td style={tdStyle}>
              <input 
                type="text" 
                className={`form-input ${errors.motherTongue ? 'form-input-error' : ''}`}
                placeholder="EXAMPLE - MARATHI" 
                style={{ marginBottom: 0 }}
                value={languages.motherTongue}
                onChange={(e) => handleLanguageChange('motherTongue', capitaliseWords(e.target.value, true))}
                onBlur={() => onBlur('motherTongue')}
              />
              {touched.motherTongue && errors.motherTongue && (
                <span style={errorStyle}>{errors.motherTongue}</span>
              )}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default CourseTable;